/**
 * The as-on date for screens that show a balance rather than a total.
 *
 * Stock and the WIP ladder are a position at one moment, so they take one
 * date. The dates people ask for are nearly always the same few — today,
 * the last month end, the last year end the auditors will ask about — so
 * those are one tap each, with any other date typed in below.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import {
  AS_ON_LABELS,
  describeDay,
  resolveAsOn,
  type AsOnPreset,
} from "../lib/period";

const ORDER: AsOnPreset[] = [
  "today",
  "yesterday",
  "end_last_month",
  "end_last_quarter",
  "end_last_fy",
  "end_last_year",
];

interface Props {
  value: string;
  onChange: (isoDate: string) => void;
}

export function AsOnPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(value);
  const [alignLeft, setAlignLeft] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    setTyped(value);
    function onPointer(e: PointerEvent) {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, value]);

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    setAlignLeft(button.current.getBoundingClientRect().right - 320 < 8);
  }, [open]);

  function pick(date: string) {
    onChange(date);
    setOpen(false);
  }

  return (
    <div className="periodpicker" ref={root}>
      <button
        ref={button}
        type="button"
        className="periodpicker__button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`As on ${describeDay(value)}`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg className="periodpicker__icon" viewBox="0 0 16 16" width="16"
             height="16" aria-hidden="true">
          <rect x="1.5" y="2.5" width="13" height="12" rx="1.5"
                fill="none" stroke="currentColor" />
          <path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" />
          <circle cx="8" cy="10.5" r="1.6" fill="currentColor" />
        </svg>
        <span className="periodpicker__value">As on {describeDay(value)}</span>
        <span className="periodpicker__caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className={`periodmenu${alignLeft ? " periodmenu--left" : ""}`}
             id={panelId} role="dialog" aria-label="As on">
          <div className="periodmenu__section">
            <span className="periodmenu__title">As on</span>
            {ORDER.map((p) => {
              const date = resolveAsOn(p);
              const active = date === value;
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={active}
                  className={`periodmenu__item${active ? " is-active" : ""}`}
                  onClick={() => pick(date)}
                >
                  <span>{AS_ON_LABELS[p]}</span>
                  <span className="periodmenu__hint">{describeDay(date)}</span>
                </button>
              );
            })}
          </div>
          <form
            className="asonpicker__date"
            onSubmit={(e) => {
              e.preventDefault();
              if (typed) pick(typed);
            }}
          >
            <label className="inputgroup inputgroup--grow">
              <span className="label">Another date</span>
              <input type="date" value={typed}
                     onChange={(e) => setTyped(e.target.value)} />
            </label>
            <button type="submit" className="btn btn--sm btn--auto" disabled={!typed}>
              Apply
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
